import AudioRecorder from '@/components/AudioRecorder';
import AutoOpenModal from '@/components/AutoOpenModal';
import TransactionNumberInput from '@/components/cadastre/TransactionNumberInput.vue';
import PriceHistoryChart from '@/components/charts/PriceHistoryChart.vue';
import NearbySalesChart from '@/components/charts/NearbySalesChart.vue';
import SalesHorizonChart from '@/components/charts/SalesHorizonChart.vue';
import InsightsChartControl from '@/components/common/InsightsChartControl.vue';
import LineChartControl from '@/components/common/LineChartControl.vue';
import MultiSelectControl from '@/components/common/MultiSelectControl.vue';
import PieChartControl from '@/components/common/PieChartControl.vue';
import ReloadContainer from '@/components/common/ReloadContainer';
import SelectControl from '@/components/common/SelectControl.vue';
import SliderControl from '@/components/common/SliderControl.vue';
import CopyToClipboard from '@/components/CopyToClipboard';
import DatePicker from '@/components/DatePicker';
import DownloadDialog from '@/components/DownloadDialog';
import EstimateDocumentDialog from '@/components/estimates/common/documentDialog/EstimateDocumentDialog.vue';
import EstateImagePicker from '@/components/estateDatabase/EstateImagePicker.vue';
import SearchEstatesForm from '@/components/estateDatabase/SearchEstatesForm.vue';
import CostAndBudget from '@/components/estimates/expertEstimate/CostAndBudget';
import ExpertEstimateForm from '@/components/estimates/ExpertEstimateForm.vue';
import PredictEstimateForm from '@/components/estimates/PredictEstimateForm.vue';
import RealEstateEstimateForm from '@/components/estimates/RealEstateEstimateForm.vue';
import Icon from '@/components/Icon.vue';
import {ClassComponentInitializer, VueComponentInitializer} from '@/components/initializers';
import LazyComponent from '@/components/LazyComponent';
import LocationInput from '@/components/LocationInput.vue';
import CadastralMapLayer from '@/components/maps/CadastralMapLayer.vue';
import CadastralUnitsMap from '@/components/maps/CadastralUnitsMap.vue';
import CadastralUnitsMapField from '@/components/maps/CadastralUnitsMapField.vue';
import EstatesMap from '@/components/maps/EstatesMap.vue';
import PredictEstatesMap from '@/components/maps/PredictEstatesMap.vue';
import EstimatesMap from '@/components/maps/EstimatesMap.vue';
import FloodZoneLayer from '@/components/maps/FloodZoneLayer.vue';
import GoogleStreetView from '@/components/maps/GoogleStreetView.vue';
import MapyCzOrthoPhoto from '@/components/maps/MapyCzOrthoPhoto.vue';
import MapyCzPanorama from '@/components/maps/MapyCzPanorama.vue';
import PointsMap from '@/components/maps/PointsMap.vue';
import ZoningPlanLayer from '@/components/maps/ZoningPlanLayer.vue';
import TownZoningPlanMap from '@/components/maps/TownZoningPlanMap.vue';
import CrimeLayer from '@/components/maps/CrimeLayer.vue';
import OverlayBaseMap from '@/components/maps/OverlayBaseMap.vue';
import CrimeProfileWidget from '@/components/crime/CrimeProfileWidget.vue';
import FloodProfileWidget from '@/components/flood/FloodProfileWidget.vue';
import EnumIcon from '@/icons/EnumIcon.vue';
import ApiTransactionFinder from '@/model/cadastre/ApiTransactionFinder';
import {EventBus} from '@/model/events';
import ApiLocationFinder from '@/model/locations/ApiLocationFinder';
import BootstrapPopover from '@/naja/BootstrapPopover';
import BootstrapTooltip from '@/naja/BootstrapTooltip';
import {PricePerMeterSync} from '@/naja/PricePerMeterSync';
import {createRouterForComponent} from '@/shared/router';
import {translationsFromAttribute} from '@/shared/translations';
import {defineAsyncComponent} from 'vue';

const provideServices = () => ({
    provide: {
        locationFinder: ApiLocationFinder.ruian(),
        eventBus: new EventBus(),
        transactionFinder: new ApiTransactionFinder(),
    },
});

const components = [
    // Vue components
    new VueComponentInitializer('#vue-real-estate-estimate-form', RealEstateEstimateForm, element => {
            const baseUri = element.getAttribute('data-base-uri');

            if (baseUri === null) {
                throw new Error('baseUri not passed to EstimateForm');
            }

            return {
                ...provideServices(),
                i18n: translationsFromAttribute(element, 'data-translations'),
                router: createRouterForComponent(element, baseUri),
            };
        },
    ),
    // Vue components
    new VueComponentInitializer('#vue-expert-estimate-form', ExpertEstimateForm, element => {
            const baseUri = element.getAttribute('data-base-uri');

            if (baseUri === null) {
                throw new Error('baseUri not passed to EstimateForm');
            }

            return {
                ...provideServices(),
                i18n: translationsFromAttribute(element, 'data-translations'),
                router: createRouterForComponent(element, baseUri),
            };
        },
    ),
    new VueComponentInitializer('#vue-predict-estimate-form', PredictEstimateForm, element => {
            const baseUri = element.getAttribute('data-base-uri');

            if (baseUri === null) {
                throw new Error('baseUri not passed to EstimateForm');
            }

            return {
                ...provideServices(),
                i18n: translationsFromAttribute(element, 'data-translations'),
                router: createRouterForComponent(element, baseUri),
            };
        },
    ),
    new VueComponentInitializer('.vue-estates-map', EstatesMap, provideServices),
    new VueComponentInitializer('.vue-predict-estates-map', PredictEstatesMap, provideServices),
    new VueComponentInitializer('.vue-location-search-ruian', LocationInput, element => {
        const locationTypes: string[] = JSON.parse(element.getAttribute('location-types') || '[]');

        if (!Array.isArray(locationTypes) || locationTypes.some(value => typeof value !== 'string')) {
            throw new Error(`Invalid value passed for locationTypes ${locationTypes.toString()}`);
        }

        return {
            provide: {
                locationFinder: ApiLocationFinder.ruian(locationTypes),
            },
        };
    }),
    new VueComponentInitializer('.vue-location-search-osm', LocationInput, element => {
        return {
            provide: {
                locationFinder: ApiLocationFinder.openStreetMap(),
            },
        };
    }),
    new VueComponentInitializer('.vue-search-estates-form', SearchEstatesForm, element => {
        const locationTypes: string[] = JSON.parse(element.getAttribute('location-types') || '[]');

        return {
            provide: {
                locationFinder: ApiLocationFinder.ruian(locationTypes),
            },
        };
    }),
    new VueComponentInitializer('.vue-estate-image-picker', EstateImagePicker),
    new VueComponentInitializer('.vue-slider-control', SliderControl),
    new VueComponentInitializer(
        '.vue-line-chart',
        LineChartControl,
        () => ({
            propsData: {
                language: 'cs-CZ',
            },
        }),
    ),
    new VueComponentInitializer('.vue-pie-chart', PieChartControl),
    new VueComponentInitializer('.vue-price-history-chart', PriceHistoryChart),
    new VueComponentInitializer('.vue-sales-horizon-chart', SalesHorizonChart),
    new VueComponentInitializer('.vue-nearby-sales-chart', NearbySalesChart),
    new VueComponentInitializer(
        '.vue-insights-chart',
        InsightsChartControl,
        () => ({
            propsData: {
                language: 'cs-CZ',
            },
        }),
    ),
    new VueComponentInitializer('.vue-multi-select-control', MultiSelectControl),
    new VueComponentInitializer('.vue-select-control', SelectControl),
    new VueComponentInitializer('.vue-cadastre-transaction-search', TransactionNumberInput, provideServices),
    new VueComponentInitializer('.vue-google-street-view', GoogleStreetView),
    new VueComponentInitializer('.vue-mapy-cz-panorama', MapyCzPanorama),
    new VueComponentInitializer('.vue-mapy-cz-ortophoto', MapyCzOrthoPhoto),
    new VueComponentInitializer('.vue-points-map', PointsMap, element => {
        return {
            i18n: translationsFromAttribute(element, 'data-translations'),
            provide: {
                locationFinder: ApiLocationFinder.ruian([]),
            },
        };
    }),
    new VueComponentInitializer('.vue-estimate-document-dialog', EstimateDocumentDialog),
    new VueComponentInitializer('.vue-cadastral-map-layer', CadastralMapLayer),
    new VueComponentInitializer('.vue-flood-zone-layer', FloodZoneLayer),
    new VueComponentInitializer('.vue-zoning-plan-layer', ZoningPlanLayer),
    new VueComponentInitializer('.vue-town-zoning-plan-map', TownZoningPlanMap),
    // The map of the zoning plan module is big; it is downloaded only on its page.
    new VueComponentInitializer('.vue-zoning-plan-explorer', defineAsyncComponent(
        () => import(/* webpackChunkName: "zoning-plan-explorer" */ '@/zoningPlan/ZoningPlanExplorer.vue'),
    )),
    new VueComponentInitializer('.vue-crime-layer', CrimeLayer),
    new VueComponentInitializer('.vue-overlay-base-map', OverlayBaseMap),
    new VueComponentInitializer('.vue-crime-profile', CrimeProfileWidget),
    new VueComponentInitializer('.vue-flood-profile', FloodProfileWidget),
    new VueComponentInitializer('.vue-cadastral-units-map-field', CadastralUnitsMapField),
    new VueComponentInitializer('.vue-cadastral-units-map', CadastralUnitsMap),
    new VueComponentInitializer('.vue-estimates-map', EstimatesMap, element => {
        return {
            i18n: translationsFromAttribute(element, 'data-translations'),
        };
    }),

    // Custom components
    new ClassComponentInitializer('.lazy-dropdown', LazyComponent, 'show.bs.dropdown'),
    new ClassComponentInitializer('.lazy-collapse', LazyComponent, 'show.bs.collapse'),
    new ClassComponentInitializer('.lazy-control', LazyComponent),
    new ClassComponentInitializer('.date, [data-provide="datepicker"]', DatePicker),
    new ClassComponentInitializer('[data-download-url]', DownloadDialog),
    new ClassComponentInitializer('[data-reload-url]', ReloadContainer),
    new ClassComponentInitializer('.audio-recorder', AudioRecorder),
    new ClassComponentInitializer('[data-copy-target]', CopyToClipboard),
    new ClassComponentInitializer('.on-load-open-modal', AutoOpenModal),
    new ClassComponentInitializer('#cost-and-budget', CostAndBudget),
    new ClassComponentInitializer('[data-bs-toggle="tooltip"]', BootstrapTooltip),
    new ClassComponentInitializer('[data-bs-toggle="popover"]', BootstrapPopover),
    new ClassComponentInitializer('form select[name="minPricePerMeter"]', PricePerMeterSync),
    new ClassComponentInitializer('form select[name="maxPricePerMeter"]', PricePerMeterSync),

    // Custom icons
    new VueComponentInitializer('[data-icon]:not(.svg-inline--fa)', Icon),
    new VueComponentInitializer('.enum-icon', EnumIcon),
];

export function bindInemComponents(snippet: HTMLElement): void {
    components.forEach(initializer => initializer.initialize(snippet));
}
